import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { IAppState } from '../../store/state';
import { selectSidenavOpened } from '../../store/selectors';
import * as CoreActions from '../../store/actions';
import {
  AdministradoraParametro,
  PARAMETRO_COR_PRIMARIA,
  PARAMETRO_COR_SECUNDARIA,
  PARAMETRO_COR_TERCIARIA,
  PARAMETRO_COR_TEXTO_PRIMARIO,
  PARAMETRO_COR_TEXTO_SECUNDARIO,
  PARAMETRO_COR_TEXTO_TERCIARIO,
} from '../../models/administradora-parametro.model';

/**
 * Defaults confirmados lendo o styles.css compilado real do app (24/08/2026)
 * — ver comentário completo em src/styles.scss.
 */
const DEFAULT_THEME: Record<string, string> = {
  '--color-primary': '#ffffff',
  '--color-secondary': '#ffffff',
  '--color-terciary': '#ffffff',
  '--text-primary-color': '#ffffff',
  '--text-secondary-color': '#ffffff',
  '--text-terciary-color': '#ffffff',
  '--tran-05': 'all .5s ease',
  '--tran-03': 'all .2s ease',
};

@Injectable({
  providedIn: 'root',
})
export class LayoutService {
  readonly sidenavOpened$ = this.store.select(selectSidenavOpened);

  constructor(private store: Store<IAppState>) {}

  /**
   * Aplica as CSS custom properties de tema a partir dos parâmetros do
   * tenant carregados no boot (ver core/store/effects.ts, efeito disparado
   * por `loadAdministradoraSuccess`). Quando `parametros` vem vazio/nulo
   * (caso real hoje para affixbeneficios em homologação — HTTP 204), aplica
   * os defaults confirmados no CSS real do app.
   *
   * CONFIRMADO lendo a resposta real de `ParametrosCompletos` para o tenant
   * `conectaplan` (24/08/2026, dados de configuração do tenant): os nomes de
   * `sParametro` (`sCorPrimaria`, `sCorSecundaria` etc., ver
   * core/models/administradora-parametro.model.ts) e os valores reais
   * (primária `#5E2E86`, secundária `#9e86cf`, terciária `#5E2E86`, texto
   * primário/terciário `#ffffff`, texto secundário `#7961AA`). O app real
   * aplica essas variáveis num `style` inline no **`<body>`**, não no
   * `<html>` — e usa `--color-secondary` (grafia correta), não
   * `--color-secundary`.
   */
  applyTheme(parametros: AdministradoraParametro[] | null): void {
    const target = document.body.style;
    const get = (nome: string, cssVar: string) =>
      parametros?.find((p) => p.sParametro === nome)?.sValor || DEFAULT_THEME[cssVar];

    target.setProperty('--color-primary', get(PARAMETRO_COR_PRIMARIA, '--color-primary'));
    target.setProperty('--color-secondary', get(PARAMETRO_COR_SECUNDARIA, '--color-secondary'));
    target.setProperty('--color-terciary', get(PARAMETRO_COR_TERCIARIA, '--color-terciary'));
    target.setProperty(
      '--text-primary-color',
      get(PARAMETRO_COR_TEXTO_PRIMARIO, '--text-primary-color')
    );
    target.setProperty(
      '--text-secondary-color',
      get(PARAMETRO_COR_TEXTO_SECUNDARIO, '--text-secondary-color')
    );
    target.setProperty(
      '--text-terciary-color',
      get(PARAMETRO_COR_TEXTO_TERCIARIO, '--text-terciary-color')
    );
  }

  toggleSidenav(): void {
    this.store.dispatch(CoreActions.toggleSidenav());
  }

  setSidenavOpened(opened: boolean): void {
    this.store.dispatch(CoreActions.setSidenavOpened({ opened }));
  }
}
